import { InputHTMLAttributes, TextareaHTMLAttributes, SelectHTMLAttributes, forwardRef, useId } from 'react';
import clsx from 'clsx';

interface FieldWrapperProps {
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
}

const fieldClasses =
  'w-full rounded-sm bg-surface border border-line text-text placeholder:text-faint ' +
  'px-3.5 py-2.5 text-base font-body transition-colors ' +
  'focus:outline-none focus:border-dawn focus-visible:outline focus-visible:outline-2 ' +
  'focus-visible:outline-dawn focus-visible:outline-offset-2';

export interface TextFieldProps
  extends FieldWrapperProps,
    InputHTMLAttributes<HTMLInputElement> {}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ label, error, hint, required, id, className, ...rest }, ref) => {
    const autoId = useId();
    const fieldId = id ?? autoId;
    const errorId = `${fieldId}-error`;
    const hintId = `${fieldId}-hint`;
    return (
      <div className="flex flex-col gap-1.5">
        <label htmlFor={fieldId} className="text-sm text-muted font-body">
          {label}
          {required && <span className="text-dawn ml-0.5">*</span>}
        </label>
        <input
          ref={ref}
          id={fieldId}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : hint ? hintId : undefined}
          className={clsx(fieldClasses, error && 'border-error', className)}
          {...rest}
        />
        {hint && !error && (
          <span id={hintId} className="text-xs text-faint">
            {hint}
          </span>
        )}
        {error && (
          <span id={errorId} role="alert" className="text-xs text-error">
            {error}
          </span>
        )}
      </div>
    );
  }
);
TextField.displayName = 'TextField';

export interface TextAreaFieldProps
  extends FieldWrapperProps,
    TextareaHTMLAttributes<HTMLTextAreaElement> {
  maxLength?: number;
}

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(
  ({ label, error, hint, required, id, className, maxLength, value, ...rest }, ref) => {
    const autoId = useId();
    const fieldId = id ?? autoId;
    const count = typeof value === 'string' ? value.length : 0;
    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between">
          <label htmlFor={fieldId} className="text-sm text-muted font-body">
            {label}
            {required && <span className="text-dawn ml-0.5">*</span>}
          </label>
          {maxLength && (
            <span className="text-xs font-mono text-faint">
              {count}/{maxLength}
            </span>
          )}
        </div>
        <textarea
          ref={ref}
          id={fieldId}
          maxLength={maxLength}
          value={value}
          className={clsx(fieldClasses, 'min-h-[120px] resize-y', error && 'border-error', className)}
          {...rest}
        />
        {hint && !error && <span className="text-xs text-faint">{hint}</span>}
        {error && (
          <span role="alert" className="text-xs text-error">
            {error}
          </span>
        )}
      </div>
    );
  }
);
TextAreaField.displayName = 'TextAreaField';

export interface SelectFieldProps
  extends FieldWrapperProps,
    SelectHTMLAttributes<HTMLSelectElement> {
  options: { value: string; label: string }[];
}

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  ({ label, error, hint, required, id, className, options, ...rest }, ref) => {
    const autoId = useId();
    const fieldId = id ?? autoId;
    return (
      <div className="flex flex-col gap-1.5">
        <label htmlFor={fieldId} className="text-sm text-muted font-body">
          {label}
          {required && <span className="text-dawn ml-0.5">*</span>}
        </label>
        <select ref={ref} id={fieldId} className={clsx(fieldClasses, error && 'border-error', className)} {...rest}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {hint && !error && <span className="text-xs text-faint">{hint}</span>}
        {error && (
          <span role="alert" className="text-xs text-error">
            {error}
          </span>
        )}
      </div>
    );
  }
);
SelectField.displayName = 'SelectField';
